import type {
    Field,
    FieldValue,
    GeneratorConfigV1,
    PricingImpact,
} from '../types/generator-config';
import { type Answers, evalVisibility, visibleFields, visibleSteps } from './visibility';

export interface PriceBreakdownEntry {
    label: string;
    delta: number;
    formula?: string;
}

export interface PriceResult {
    total: number;
    base: number;
    entries: PriceBreakdownEntry[];
    currency: string;
}

/**
 * Calcule le prix total à partir de la config et des réponses.
 * Parcours les fields visibles, applique leur pricingImpact, puis
 * les PricingRule globales dont la condition est remplie.
 *
 * Les `multiply` s'appliquent sur le total cumulé au moment de leur
 * évaluation (add/addPerUnit/lookup d'abord, puis multiply).
 */
export function computePrice(config: GeneratorConfigV1, answers: Answers): PriceResult {
    const { pricing, steps, branding } = config;
    const entries: PriceBreakdownEntry[] = [];
    let total = pricing.baseAmount;

    const addsFirst: Array<{ label: string; impact: Exclude<PricingImpact, { type: 'multiply' }>; value: FieldValue; optionsLookup?: Record<string, string> }> = [];
    const multiplies: Array<{ label: string; impact: Extract<PricingImpact, { type: 'multiply' }> }> = [];

    const filteredSteps = visibleSteps(steps, answers);
    for (const step of filteredSteps) {
        const fields = visibleFields(step.fields, answers);
        for (const field of fields) {
            if (!field.pricing || field.pricing.type === 'none') continue;
            const value = answers[field.id];
            if (value === null || value === undefined || value === '') continue;

            if (field.pricing.type === 'multiply') {
                multiplies.push({ label: labelFor(field, value), impact: field.pricing });
            } else {
                addsFirst.push({
                    label: labelFor(field, value),
                    impact: field.pricing,
                    value,
                    optionsLookup: lookupOptions(field),
                });
            }
        }
    }

    for (const { label, impact, value, optionsLookup } of addsFirst) {
        const delta = applyNonMultiply(impact, value, optionsLookup);
        if (delta !== 0) {
            entries.push({ label, delta });
            total += delta;
        }
    }

    for (const { label, impact } of multiplies) {
        const before = total;
        total = total * impact.factor;
        const delta = total - before;
        if (delta !== 0) {
            entries.push({ label: `${label} (×${impact.factor})`, delta });
        }
    }

    // Règles globales
    for (const rule of pricing.rules) {
        if (!evalVisibility(rule.condition, answers)) continue;
        if (rule.effect.type === 'multiply') {
            const before = total;
            total = total * rule.effect.factor;
            const delta = total - before;
            if (delta !== 0) entries.push({ label: rule.label, delta });
        } else {
            const delta = applyNonMultiply(rule.effect, null, undefined);
            if (delta !== 0) {
                entries.push({ label: rule.label, delta });
                total += delta;
            }
        }
    }

    return {
        total: Math.max(0, Math.round(total * 100) / 100),
        base: pricing.baseAmount,
        entries,
        currency: branding.currency,
    };
}

function applyNonMultiply(
    impact: Exclude<PricingImpact, { type: 'multiply' }>,
    value: FieldValue,
    optionsLookup: Record<string, string> | undefined
): number {
    switch (impact.type) {
        case 'none':
            return 0;
        case 'add':
            return impact.amount;
        case 'addPerUnit': {
            const n = typeof value === 'number' ? value : Number(value);
            return Number.isFinite(n) ? n * impact.unitPrice : 0;
        }
        case 'lookup': {
            if (Array.isArray(value)) {
                return value.reduce<number>((acc, id) => acc + (impact.amounts[String(id)] ?? 0), 0);
            }
            if (value !== null && value !== undefined) {
                return impact.amounts[String(value)] ?? 0;
            }
            return 0;
        }
        case 'ranges': {
            const n = typeof value === 'number' ? value : Number(value);
            if (!Number.isFinite(n)) return 0;
            const match = impact.ranges.find(
                (r) => n >= r.min && (r.max === null || n < r.max)
            );
            return match?.amount ?? 0;
        }
        default:
            void optionsLookup;
            return 0;
    }
}

function labelFor(field: Field, value: FieldValue): string {
    if (Array.isArray(value)) return field.label;
    if (typeof value === 'number') return `${field.label} : ${value}`;
    if (typeof value === 'string' && 'options' in field && Array.isArray((field as { options?: { id: string; label: string }[] }).options)) {
        const opts = (field as { options: { id: string; label: string }[] }).options;
        const match = opts.find((o) => o.id === value);
        if (match) return `${field.label} : ${match.label}`;
    }
    return field.label;
}

function lookupOptions(field: Field): Record<string, string> | undefined {
    if ('options' in field && Array.isArray((field as { options?: unknown }).options)) {
        const opts = (field as { options: Array<{ id: string; label: string }> }).options;
        return Object.fromEntries(opts.map((o) => [o.id, o.label]));
    }
    return undefined;
}

/** Formate un montant selon la devise, en FR */
export function formatPrice(amount: number, currency: string): string {
    try {
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency,
            maximumFractionDigits: 0,
        }).format(amount);
    } catch {
        return `${Math.round(amount)} ${currency}`;
    }
}
